import {
  type FocusEvent as ReactFocusEvent,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  type Kontaktkarte as Karte,
  type Vorschaukanal,
  kontaktkarte as ladeKontaktkarte,
} from '../api/kunden';
import { Kanalliste, VORSCHAU_RUHE_MS } from '../kunde/Kundenvorschau';
import { Kundenmarke } from '../kunde/Kundenmarke';
import { markenklasse } from '../marken';
import { Schliesskreuz } from './Schliesskreuz';
import { Sinnbild } from './Sinnbild';

/**
 * DIE KONTAKTKARTE AM NAMEN (Auftrag CRM-181).
 *
 * Ulf (10.10.2026): „In den Ansichten Kunde, Angebot und Auftrag steht immer
 * ein Ansprechpartner oder eine Firma/Kontakt. Öffne beim Überfahren ein
 * kleines Fenster mit den Kontaktdaten."
 *
 * `<KundeVerweis>` legt sich um einen vorhandenen Namen oder Verweis und
 * ändert ihn nicht: Der Klick öffnet weiter, was er bisher öffnete. Die Karte
 * kommt dazu — und mit ihr die gestrichelte Linie (`.verweis--fenster`,
 * CRM-189), die einen Verweis mit kleinem Fenster von einem reinen Sprung
 * unterscheidet.
 *
 *   RECHT. Ohne `zurAkte` gibt es keine Karte — der Name bleibt, wie er war.
 *   Den Weg zur Akte setzt die Arbeitsfläche nur mit `kunde:sehen` (BEN-02,
 *   `Aktenwege`); so hängen Karte und Verweis an DEMSELBEN Recht. Der Server
 *   prüft es ein zweites Mal samt Sichtbarkeitsbereich (SYS-02).
 *
 *   KEINE ANFRAGE BEIM LADEN. Gefragt wird erst, wenn die Karte aufgeht: nach
 *   `VORSCHAU_RUHE_MS` Ruhe des Zeigers oder des Fokus, auf dem Tablet auf
 *   ausdrückliches Verlangen. Eine Liste mit 200 Namen fragt beim Laden nichts.
 *
 *   EIN SPEICHER FÜR ALLE. Die Antworten liegen in EINER Ablage dieses Moduls,
 *   60 s wie die Vorschau der Kundenliste. Derselbe Kunde in Kopf, Liste und
 *   Karte „Verbunden" wird einmal gefragt; eine laufende Anfrage wird geteilt.
 *
 *   MAUS. Überfahren mit Ruhezeit öffnet; die Karte bleibt offen, solange der
 *   Zeiger auf Name oder Karte steht, und schließt nach `NACHLAUF_MS`.
 *
 *   TASTATUR (NFA-14, WCAG 1.4.13). Der Fokus auf dem Namen öffnet nach der
 *   Ruhezeit, Tab führt in die Karte, Tab hinaus schließt und gibt den Fokus
 *   an den Namen zurück. Escape schließt, auch ohne Fokus in der Karte.
 *
 *   TABLET (CRM-106, NFA-07). Kein Überfahren: Der Tipp öffnet weiter die
 *   Akte. Das Zeichen „info" neben dem Namen und langes Drücken öffnen die
 *   Karte; Tippen daneben und das Kreuz schließen. Mit Maus und Trackpad
 *   blendet das Stilblatt das Zeichen aus.
 *
 *   IM BILD. Die Karte hängt am Ende von `body` und steht `fixed` — kein
 *   Überlauf einer Tabelle und kein Dialog schneidet sie ab. Lage wie die
 *   Titelkarte (CRM-56): unter dem Namen, bei zu wenig Platz darüber, nie
 *   über einen Rand. Klicks in der Karte steigen nicht in die Zeile darunter.
 *
 *   DATENSPARSAMKEIT. Nur Kontakt, Marke, Sperre, Werbewiderspruch und
 *   „ausgeschieden" — keine Umsätze, Buchungen oder Konditionen.
 *
 * Die Postfächer (CRM-176 bis CRM-179) sollen den Absender eines Vorgangs mit
 * dieser Komponente zeigen, sobald dort ein Kunde zugeordnet ist.
 */

/** Wie lange die Karte nach dem Verlassen noch steht — der Weg vom Namen in die Karte. */
export const NACHLAUF_MS = 300;
/** Ab so langem Drücken mit Finger oder Stift öffnet die Karte (CRM-106). */
export const LANGDRUCK_MS = 500;
/** Wie lange eine geladene Karte gilt — wie die Vorschau der Kundenliste. */
const GILT_MS = 60_000;

const ABSTAND = 4;
const RAND = 8;
const MINDESTHOEHE = 200;

/* ── Der geteilte Speicher ─────────────────────────────────────────────── */

const ablage = new Map<string, { readonly daten: Karte; readonly zeit: number }>();
const unterwegs = new Map<string, Promise<Karte>>();

function schluessel(kunde: string, ansprechpartner: string | null): string {
  return `${kunde}|${ansprechpartner ?? ''}`;
}

/** Eine Karte aus der Ablage oder vom Server — eine laufende Anfrage wird geteilt. */
export function holeKontaktkarte(kunde: string, ansprechpartner: string | null): Promise<Karte> {
  const s = schluessel(kunde, ansprechpartner);
  const gemerkt = ablage.get(s);
  if (gemerkt !== undefined && Date.now() - gemerkt.zeit < GILT_MS) return Promise.resolve(gemerkt.daten);
  const laufend = unterwegs.get(s);
  if (laufend !== undefined) return laufend;
  const anfrage = ladeKontaktkarte(kunde, ansprechpartner)
    .then((daten) => {
      ablage.set(s, { daten, zeit: Date.now() });
      return daten;
    })
    .finally(() => unterwegs.delete(s));
  unterwegs.set(s, anfrage);
  return anfrage;
}

/** Für die Prüfungen: jede Prüfung beginnt mit leerem Speicher. */
export function vergissKontaktkarten(): void {
  ablage.clear();
  unterwegs.clear();
}

type Zustand =
  | { readonly art: 'laedt' }
  | { readonly art: 'da'; readonly daten: Karte }
  | { readonly art: 'fehler' };

function useKarte(kunde: string, ansprechpartner: string | null, offen: boolean): Zustand {
  const [stand, setzeStand] = useState<{ readonly s: string; readonly zustand: Zustand } | undefined>(undefined);
  const s = schluessel(kunde, ansprechpartner);
  useEffect(() => {
    if (!offen) return undefined;
    let gemeint = true;
    holeKontaktkarte(kunde, ansprechpartner).then(
      (daten) => {
        if (gemeint) setzeStand({ s, zustand: { art: 'da', daten } });
      },
      () => {
        if (gemeint) setzeStand({ s, zustand: { art: 'fehler' } });
      },
    );
    return () => {
      gemeint = false;
    };
  }, [offen, kunde, ansprechpartner, s]);
  return stand?.s === s ? stand.zustand : { art: 'laedt' };
}

/* ── Der Inhalt ────────────────────────────────────────────────────────── */

/* KUN-08: die Rollen einer Kontaktrolle, wie die Akte sie nennt. */
const ROLLENNAME: Readonly<Record<string, string>> = {
  ansprechpartner: 'Ansprechpartner',
  geschaeftsfuehrung: 'Geschäftsführung',
  einkauf: 'Einkauf',
  buchhaltung: 'Buchhaltung',
  technik: 'Technik',
  vermittler: 'Vermittler',
};

function telefonVerweis(wert: string): string {
  return `tel:${wert.replace(/[^\d+]/g, '')}`;
}

function anschriftZeilen(adresse: Karte['kontakt']['adresse']): readonly string[] {
  if (adresse === null) return [];
  return [
    ...(adresse.zusatz === null ? [] : [adresse.zusatz]),
    adresse.strasse,
    `${adresse.plz} ${adresse.ort}${adresse.land === 'DE' ? '' : `, ${adresse.land}`}`,
  ];
}

/** Was „Kopieren" in die Zwischenablage legt: Name, Firma, Anschrift, Kanäle — Zeile für Zeile. */
export function kartentext(k: Karte): string {
  const ap = k.ansprechpartner;
  const kanaele = (liste: readonly Vorschaukanal[]): string[] =>
    liste.map((c) => `${c.art === 'email' ? 'E-Mail' : c.art === 'mobil' ? 'Mobil' : 'Telefon'}: ${c.wert}`);
  return [
    ...(ap === null ? [] : [ap.name, ...(ap.funktion === null ? [] : [ap.funktion])]),
    k.name,
    ...anschriftZeilen(k.kontakt.adresse),
    ...kanaele(ap === null ? k.kontakt.kanaele : ap.kanaele),
  ].join('\n');
}

function erster(liste: readonly Vorschaukanal[], arten: readonly Vorschaukanal['art'][]): Vorschaukanal | undefined {
  return liste.find((c) => arten.includes(c.art));
}

function Inhalt({
  daten,
  zurAkte,
  schliessen,
}: {
  readonly daten: Karte;
  readonly zurAkte: (id: string) => void;
  readonly schliessen: () => void;
}): JSX.Element {
  const [kopiert, setzeKopiert] = useState<'ja' | 'nein' | undefined>(undefined);
  const ap = daten.ansprechpartner;
  const haupt = daten.kontakt.ansprechpartner;
  /* Wer am Ansprechpartner fragt, ruft IHN an; fehlt ihm ein Kanal, die Firma. */
  const reihen: readonly (readonly Vorschaukanal[])[] =
    ap === null ? [daten.kontakt.kanaele, haupt?.kanaele ?? []] : [ap.kanaele, daten.kontakt.kanaele];
  const anruf = reihen.map((r) => erster(r, ['telefon', 'mobil'])).find((c) => c !== undefined);
  const post = reihen.map((r) => erster(r, ['email'])).find((c) => c !== undefined);
  const werbung = ap === null ? daten.werbeerlaubnis : ap.werbeerlaubnis;
  const anschrift = anschriftZeilen(daten.kontakt.adresse);

  return (
    <>
      <div className="kontaktkarte__kopf">
        <strong>{ap === null ? daten.name : ap.name}</strong>
        {ap === null && <small>{daten.kundennummer}</small>}
      </div>
      <div className="kontaktkarte__marken">
        {ap === null && <Kundenmarke marke={daten.marke} art={daten.kostentraegerart ?? undefined} />}
        {/* DAT-25: Ausgeschiedene bleiben am Beleg stehen — und tragen es sichtbar. */}
        {ap?.ausgeschieden === true && <span className={markenklasse('stillgelegt')}>ausgeschieden</span>}
        {/* KUN-17: nur das Kennzeichen; der Grund steht in der Akte. */}
        {daten.gesperrt && <span className={markenklasse('sackgasse')}>gesperrt</span>}
        {/* LEG-12: ein Widerspruch steht sichtbar da, damit niemand wirbt. */}
        {werbung === 'widerspruch' && <span className={markenklasse('sackgasse')}>Werbewiderspruch</span>}
      </div>
      <dl className="angaben kontaktkarte__angaben">
        {ap !== null && (ap.funktion !== null || ap.abteilung !== null || ap.rolle !== '') && (
          <>
            <dt>Funktion</dt>
            <dd>
              {[ap.funktion ?? ROLLENNAME[ap.rolle] ?? ap.rolle, ap.abteilung].filter((t) => t !== null).join(', ')}
            </dd>
          </>
        )}
        <dt>Erreichbar</dt>
        <dd>
          {(ap === null ? daten.kontakt.kanaele : ap.kanaele).length === 0 ? (
            <span className="feld__hilfe">kein Telefon und keine E-Mail hinterlegt</span>
          ) : (
            <Kanalliste kanaele={ap === null ? daten.kontakt.kanaele : ap.kanaele} />
          )}
        </dd>
        {ap !== null && (
          <>
            <dt>Firma</dt>
            <dd>
              {daten.name} <small className="feld__hilfe">{daten.kundennummer}</small>
            </dd>
          </>
        )}
        <dt>Anschrift</dt>
        <dd>
          {anschrift.length === 0 ? (
            <span className="feld__hilfe">keine Anschrift hinterlegt</span>
          ) : (
            anschrift.map((z) => <div key={z}>{z}</div>)
          )}
        </dd>
        {/* KUN-08: Firmen und öffentliche Auftraggeber führen Ansprechpartner. */}
        {ap === null && daten.kundenart !== 'privat' && (
          <>
            <dt>Ansprechpartner</dt>
            <dd>
              {haupt === null ? (
                <span className="feld__hilfe">kein Ansprechpartner hinterlegt</span>
              ) : (
                <>
                  {haupt.name}
                  {haupt.funktion !== null && `, ${haupt.funktion}`}
                  {haupt.kanaele.length > 0 && <Kanalliste kanaele={haupt.kanaele} />}
                  {daten.kontakt.weitereAnsprechpartner > 0 && (
                    <small className="feld__hilfe">
                      {` und ${String(daten.kontakt.weitereAnsprechpartner)} weitere in der Akte`}
                    </small>
                  )}
                </>
              )}
            </dd>
          </>
        )}
      </dl>
      <div className="knopfreihe kontaktkarte__handlungen">
        {/* KUN-04: anklickbar wie in Akte und Vorschau. */}
        {anruf !== undefined && (
          <a className="knopf knopf--klein" href={telefonVerweis(anruf.wert)}>
            Anrufen
          </a>
        )}
        {post !== undefined && (
          <a className="knopf knopf--klein" href={`mailto:${post.wert}`}>
            E-Mail
          </a>
        )}
        <button
          type="button"
          className="knopf knopf--klein"
          onClick={() => {
            const ablageSchreiben = navigator.clipboard?.writeText(kartentext(daten));
            if (ablageSchreiben === undefined) {
              setzeKopiert('nein');
              return;
            }
            ablageSchreiben.then(
              () => setzeKopiert('ja'),
              () => setzeKopiert('nein'),
            );
          }}
        >
          Kopieren
        </button>
        <button
          type="button"
          className="knopf knopf--klein"
          onClick={() => {
            schliessen();
            zurAkte(daten.id);
          }}
        >
          Akte öffnen
        </button>
      </div>
      <p className="nur-vorlesen" role="status">
        {kopiert === 'ja' ? 'Kontaktdaten kopiert' : kopiert === 'nein' ? 'Kopieren nicht möglich' : ''}
      </p>
      {kopiert !== undefined && (
        <p className="feld__hilfe" aria-hidden="true">
          {kopiert === 'ja' ? 'Kopiert.' : 'Die Zwischenablage ist hier nicht erreichbar.'}
        </p>
      )}
    </>
  );
}

/* ── Die Hülle ─────────────────────────────────────────────────────────── */

const BEDIENBAR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function bedienbare(wurzel: HTMLElement | null): HTMLElement[] {
  return wurzel === null ? [] : [...wurzel.querySelectorAll<HTMLElement>(BEDIENBAR)];
}

/**
 * Ein Kunden- oder Ansprechpartnername mit Kontaktkarte.
 *
 * `zurAkte` ist zugleich das Recht: fehlt er, bleibt `children` unverändert.
 */
export function KundeVerweis({
  kundeId,
  ansprechpartnerId = null,
  zurAkte,
  children,
}: {
  readonly kundeId: string | null | undefined;
  /** Die Kontaktrolle (KUN-08), wenn der Name ein Ansprechpartner ist. */
  readonly ansprechpartnerId?: string | null | undefined;
  /** Der Weg zur Kundenakte — nur mit `kunde:sehen` gesetzt. */
  readonly zurAkte: ((id: string) => void) | undefined;
  readonly children: ReactNode;
}): JSX.Element {
  if (zurAkte === undefined || kundeId === null || kundeId === undefined || kundeId === '') return <>{children}</>;
  return (
    <MitKarte kundeId={kundeId} ansprechpartnerId={ansprechpartnerId} zurAkte={zurAkte}>
      {children}
    </MitKarte>
  );
}

function MitKarte({
  kundeId,
  ansprechpartnerId,
  zurAkte,
  children,
}: {
  readonly kundeId: string;
  readonly ansprechpartnerId: string | null;
  readonly zurAkte: (id: string) => void;
  readonly children: ReactNode;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);
  const kartenId = `kontaktkarte-${useId()}`;
  const anker = useRef<HTMLSpanElement>(null);
  const flaeche = useRef<HTMLDivElement>(null);
  const uhr = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const druck = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /* Langes Drücken hat geöffnet: Der Klick, der dem Loslassen folgt, öffnet nicht die Akte. */
  const klickSchlucken = useRef(false);
  /* Der Fokus kommt aus der Karte zurück — er soll nicht gleich wieder öffnen. */
  const stillerFokus = useRef(false);
  const zustand = useKarte(kundeId, ansprechpartnerId, offen);

  const anhalten = useCallback((): void => {
    if (uhr.current !== undefined) clearTimeout(uhr.current);
    uhr.current = undefined;
  }, []);
  const spaeter = useCallback(
    (ms: number, was: () => void): void => {
      anhalten();
      uhr.current = setTimeout(() => {
        uhr.current = undefined;
        was();
      }, ms);
    },
    [anhalten],
  );
  const schliessen = useCallback((): void => {
    anhalten();
    setzeOffen(false);
  }, [anhalten]);
  useEffect(
    () => () => {
      anhalten();
      if (druck.current !== undefined) clearTimeout(druck.current);
    },
    [anhalten],
  );

  /** Den Fokus an den Namen zurück, ohne dass er wieder öffnet. */
  const fokusZurueck = useCallback((): void => {
    const ziel = bedienbare(anker.current)[0];
    if (ziel === undefined) return;
    stillerFokus.current = true;
    ziel.focus();
  }, []);

  useEffect(() => {
    if (!offen) return undefined;
    /* WCAG 1.4.13: Escape schließt auch ohne Fokus; was ein Dialog schon nahm, gilt dort. */
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      const fokusDrin = flaeche.current?.contains(document.activeElement) === true;
      schliessen();
      if (fokusDrin) fokusZurueck();
    };
    /* Daneben getippt oder geklickt: zu. Name und Karte zählen als drinnen. */
    const beiDruck = (e: PointerEvent): void => {
      const ziel = e.target as Node;
      if (anker.current?.contains(ziel) !== true && flaeche.current?.contains(ziel) !== true) schliessen();
    };
    window.addEventListener('keydown', beiTaste);
    document.addEventListener('pointerdown', beiDruck);
    return () => {
      window.removeEventListener('keydown', beiTaste);
      document.removeEventListener('pointerdown', beiDruck);
    };
  }, [offen, schliessen, fokusZurueck]);

  /* Die Lage vor dem Zeichnen — wie die Titelkarte (CRM-56). */
  useLayoutEffect(() => {
    if (!offen) return undefined;
    const legen = (): void => {
      const r = anker.current?.getBoundingClientRect();
      const f = flaeche.current;
      if (r === undefined || f === null) return;
      const unten = window.innerHeight - r.bottom - ABSTAND - RAND;
      const oben = r.top - ABSTAND - RAND;
      const nachOben = unten < MINDESTHOEHE && oben > unten;
      f.style.left = `${String(Math.max(RAND, Math.min(r.left, window.innerWidth - RAND - f.offsetWidth)))}px`;
      f.style.top = nachOben ? '' : `${String(r.bottom + ABSTAND)}px`;
      f.style.bottom = nachOben ? `${String(window.innerHeight - r.top + ABSTAND)}px` : '';
      f.style.maxHeight = `${String(Math.max(0, nachOben ? oben : unten))}px`;
    };
    legen();
    window.addEventListener('resize', legen);
    window.addEventListener('scroll', legen, true);
    return () => {
      window.removeEventListener('resize', legen);
      window.removeEventListener('scroll', legen, true);
    };
  }, [offen, zustand.art]);

  const zeigerKommt = (e: ReactPointerEvent): void => {
    if (e.pointerType !== 'mouse') return;
    if (offen) anhalten();
    else spaeter(VORSCHAU_RUHE_MS, () => setzeOffen(true));
  };
  const zeigerGeht = (e: ReactPointerEvent): void => {
    if (e.pointerType !== 'mouse') return;
    spaeter(NACHLAUF_MS, () => setzeOffen(false));
  };
  const drueckenEnde = (): void => {
    if (druck.current !== undefined) clearTimeout(druck.current);
    druck.current = undefined;
  };

  const fokusVerlassen = (e: ReactFocusEvent): void => {
    /* Ohne neues Ziel (Safari beim Klick) entscheidet der Zeiger. */
    const ziel = e.relatedTarget;
    if (ziel === null) return;
    if (anker.current?.contains(ziel) === true || flaeche.current?.contains(ziel) === true) return;
    if (offen) schliessen();
    else anhalten();
  };

  const tasteAmNamen = (e: ReactKeyboardEvent): void => {
    /* Tab führt vom Namen in die offene Karte. */
    if (e.key !== 'Tab' || e.shiftKey || !offen) return;
    const erstes = bedienbare(flaeche.current)[0];
    if (erstes === undefined) return;
    e.preventDefault();
    erstes.focus();
  };

  const tasteInKarte = (e: ReactKeyboardEvent): void => {
    if (e.key !== 'Tab') return;
    const liste = bedienbare(flaeche.current);
    const randElement = e.shiftKey ? liste[0] : liste[liste.length - 1];
    /* Tab über den Rand der Karte: zu, und der Fokus steht wieder am Namen. */
    if (document.activeElement === randElement) {
      e.preventDefault();
      schliessen();
      fokusZurueck();
    }
  };

  const titel =
    zustand.art === 'da'
      ? `Kontaktkarte ${zustand.daten.ansprechpartner?.name ?? zustand.daten.name}`
      : 'Kontaktkarte';

  return (
    <span
      ref={anker}
      className="kontaktkarte__anker verweis--fenster"
      onPointerEnter={zeigerKommt}
      onPointerLeave={zeigerGeht}
      onPointerDown={(e) => {
        if (e.pointerType === 'mouse') return;
        drueckenEnde();
        druck.current = setTimeout(() => {
          druck.current = undefined;
          klickSchlucken.current = true;
          setzeOffen(true);
        }, LANGDRUCK_MS);
      }}
      onPointerUp={drueckenEnde}
      onPointerCancel={drueckenEnde}
      onContextMenu={(e) => {
        /* Das Kontextmenü des langen Drückens weicht der Karte. */
        if (klickSchlucken.current || druck.current !== undefined) e.preventDefault();
      }}
      onClickCapture={(e) => {
        if (!klickSchlucken.current) return;
        klickSchlucken.current = false;
        e.preventDefault();
        e.stopPropagation();
      }}
      onFocus={(e) => {
        if (stillerFokus.current) {
          stillerFokus.current = false;
          return;
        }
        if ((e.target as HTMLElement).classList.contains('kontaktkarte__info') || offen) return;
        spaeter(VORSCHAU_RUHE_MS, () => setzeOffen(true));
      }}
      onBlur={fokusVerlassen}
      onKeyDown={tasteAmNamen}
    >
      {children}
      <button
        type="button"
        className="kontaktkarte__info"
        aria-label="Kontaktkarte"
        aria-expanded={offen}
        aria-controls={offen ? kartenId : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (offen) schliessen();
          else {
            anhalten();
            setzeOffen(true);
          }
        }}
      >
        <Sinnbild name="info" gewicht="light" />
      </button>
      {offen &&
        createPortal(
          /* Klicks und Tasten bleiben in der Karte — die Zeile darunter öffnet sonst ihre Akte. */
          <div
            id={kartenId}
            ref={flaeche}
            role="dialog"
            aria-label={titel}
            className="aufklapper__liste titelkarte__karte kontaktkarte"
            onPointerEnter={zeigerKommt}
            onPointerLeave={zeigerGeht}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              tasteInKarte(e);
            }}
            onFocus={(e) => e.stopPropagation()}
            onBlur={(e) => {
              e.stopPropagation();
              fokusVerlassen(e);
            }}
          >
            {zustand.art === 'laedt' && (
              <p className="feld__hilfe" role="status">
                Kontaktdaten werden geladen …
              </p>
            )}
            {zustand.art === 'fehler' && (
              <p className="warnung" role="status">
                Die Kontaktdaten ließen sich nicht laden.
              </p>
            )}
            {zustand.art === 'da' && <Inhalt daten={zustand.daten} zurAkte={zurAkte} schliessen={schliessen} />}
            <Schliesskreuz
              beiKlick={() => {
                schliessen();
                fokusZurueck();
              }}
            />
          </div>,
          document.body,
        )}
    </span>
  );
}
